
import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Upload, FileUp, AlertCircle } from "lucide-react";
import Papa from "papaparse";
import { toast } from "sonner";
import { showErrorToast } from "@/utils/errorHandling";
import { Alert, AlertDescription } from "@/components/ui/alert";
import type { ImportDataType, CSVRowData } from "../types";

interface CSVImportFormProps {
  onDataUploaded: (data: CSVRowData[], headers: string[]) => void;
  dataType: ImportDataType;
}

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB limit
const MIN_ROWS = 1;
const MAX_ROWS = 1000;

export function CSVImportForm({ onDataUploaded, dataType }: CSVImportFormProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFileSize = (file: File): boolean => {
    if (file.size > MAX_FILE_SIZE) {
      setError(`File size exceeds ${MAX_FILE_SIZE / 1024 / 1024}MB limit`);
      return false;
    }
    return true;
  };

  const validateFileType = (file: File): boolean => {
    const validTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/csv',
      'application/x-csv',
      'text/x-csv',
      'text/comma-separated-values',
      'text/x-comma-separated-values'
    ];

    if (!validTypes.includes(file.type) && !file.name.endsWith('.csv')) {
      setError('Please upload a valid CSV file');
      return false;
    }
    return true;
  };

  const handleFileUpload = (file: File) => {
    if (!file) return;
    
    setError(null);
    setIsProcessing(true);

    // Validate file type and size
    if (!validateFileType(file) || !validateFileSize(file)) {
      setIsProcessing(false);
      return;
    }

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        setIsProcessing(false);
        console.log('CSV parsing results:', results);

        // Validate parsing errors
        if (results.errors.length > 0) {
          const errorMessages = results.errors.map(err => 
            `Row ${err.row + 1}: ${err.message}`
          ).join('\n');
          setError(`CSV parsing errors:\n${errorMessages}`);
          console.error('CSV parsing errors:', results.errors);
          return;
        }

        // Validate row count
        if (results.data.length === 0) {
          setError('The CSV file is empty. Please add some data.');
          return;
        }

        if (results.data.length > MAX_ROWS) {
          setError(`File contains too many rows. Maximum allowed is ${MAX_ROWS} rows.`);
          return;
        }

        // Validate headers
        const headers = results.meta.fields || [];
        if (headers.length === 0) {
          setError('Could not detect column headers in the CSV file.');
          return;
        }

        // Validate required columns based on data type
        const requiredColumns = dataType === 'clients' 
          ? ['name'] 
          : ['first_name', 'last_name', 'email'];

        const missingColumns = requiredColumns.filter(col => 
          !headers.includes(col)
        );

        if (missingColumns.length > 0) {
          setError(`Missing required columns: ${missingColumns.join(', ')}`);
          return;
        }

        // Cast data to expected format
        const data = results.data as CSVRowData[];
        
        // Additional data validation
        const validationErrors: string[] = [];
        data.forEach((row, index) => {
          if (dataType === 'clients' && !row.name?.trim()) {
            validationErrors.push(`Row ${index + 1}: Client name is required`);
          }
          if (dataType === 'candidates') {
            if (!row.first_name?.trim()) validationErrors.push(`Row ${index + 1}: First name is required`);
            if (!row.last_name?.trim()) validationErrors.push(`Row ${index + 1}: Last name is required`);
            if (!row.email?.trim()) validationErrors.push(`Row ${index + 1}: Email is required`);
          }
        });

        if (validationErrors.length > 0) {
          setError(`Validation errors:\n${validationErrors.join('\n')}`);
          return;
        }

        onDataUploaded(data, headers);
        toast.success(`Successfully parsed ${data.length} rows from CSV`);
      },
      error: (error) => {
        setIsProcessing(false);
        showErrorToast(error, 'Failed to parse CSV file');
        console.error('CSV parsing error:', error);
      }
    });
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="w-full space-y-4">
      <div
        className={`border-2 border-dashed rounded-lg p-10 text-center ${
          isDragging ? 'border-primary bg-primary/5' : 'border-gray-300'
        } transition-colors duration-200 cursor-pointer`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleButtonClick}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleChange}
          accept=".csv"
          className="hidden"
        />
        
        <div className="flex flex-col items-center justify-center space-y-3">
          <div className="bg-primary/10 p-3 rounded-full">
            <Upload className="h-8 w-8 text-primary" />
          </div>
          <div className="space-y-1">
            <p className="text-lg font-medium">Drag & drop your CSV file here</p>
            <p className="text-sm text-muted-foreground">
              Maximum file size: {MAX_FILE_SIZE / 1024 / 1024}MB
            </p>
            <p className="text-sm text-muted-foreground">
              Supported format: CSV
            </p>
          </div>
          <Button variant="outline" className="mt-2" type="button">
            <FileUp className="mr-2 h-4 w-4" />
            Upload CSV
          </Button>
        </div>
      </div>
      
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="whitespace-pre-line">
            {error}
          </AlertDescription>
        </Alert>
      )}
      
      {isProcessing && (
        <div className="flex justify-center">
          <div className="animate-pulse text-center">
            <p>Processing CSV file...</p>
          </div>
        </div>
      )}
    </div>
  );
}
